import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../contexts/ApiContext';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './AnalyticsDashboard.css';

interface AnalyticsDashboardProps {
  documentId: string;
  isAnonymous?: boolean;
}

interface DocumentAnalytics {
  document_id: string;
  total_views: number;
  unique_viewers: number;
  total_downloads: number;
  total_prints: number;
  total_reshares: number;
  avg_pages_per_session: number;
  completion_rate: number;
  avg_session_duration: number;
  most_viewed_pages: Array<{ page_number: number; view_count: number }>;
  drop_off_pages: number[];
  geographic_distribution: Record<string, number>;
  activity_timeline: Array<{
    timestamp: string;
    view_count: number;
    download_count: number;
    print_count: number;
  }>;
}

interface ViewerSummary {
  user_id: string;
  email: string;
  display_name?: string;
  total_page_views: number;
  avg_time_per_page_ms: number;
  total_session_duration_ms: number;
  last_viewed_at: string;
  pages_viewed: Array<{ page_number: number; view_count: number; avg_duration_ms: number }>;
}

interface PendingViewer {
  viewer_id: string;
  viewer_email: string;
  granted_at: string;
  granted_by: string;
  can_view: boolean;
  can_download: boolean;
  can_print: boolean;
}

const COLORS = ['#2c5aa0', '#4a90e2', '#7bb3f0', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899'];

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  documentId,
  isAnonymous = false
}) => {
  const { analytics, permissions } = useApi();
  const [analyticsData, setAnalyticsData] = useState<DocumentAnalytics | null>(null);
  const [viewers, setViewers] = useState<ViewerSummary[]>([]);
  const [pendingViewers, setPendingViewers] = useState<PendingViewer[]>([]);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [resharingId, setResharingId] = useState<string | null>(null);
  const [reshareError, setReshareError] = useState<string | null>(null);
  const [reshareSuccess, setReshareSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);
        const [data, viewerData, pendingData] = await Promise.all([
          analytics.getDocumentAnalytics(documentId),
          analytics.getDocumentViewers(documentId).catch(() => []),
          analytics.getPendingViewers(documentId).catch(() => ({ pending_viewers: [] })),
        ]);
        setAnalyticsData(data);
        setViewers(viewerData ?? []);
        setPendingViewers(pendingData?.pending_viewers ?? []);
      } catch (err) {
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [documentId, analytics]);

  const toggleUser = useCallback((userId: string) => {
    setExpandedUser(prev => prev === userId ? null : userId);
  }, []);

  const handleReshare = useCallback(async (viewer: PendingViewer) => {
    setResharingId(viewer.viewer_id);
    setReshareError(null);
    setReshareSuccess(null);
    try {
      await permissions.shareDocument(documentId, viewer.viewer_id, {
        canView: viewer.can_view,
        canDownload: viewer.can_download,
        canPrint: viewer.can_print,
        watermark: false,
      });
      setReshareSuccess(viewer.viewer_id);
      setTimeout(() => setReshareSuccess(null), 3000);
    } catch {
      setReshareError(viewer.viewer_id);
      setTimeout(() => setReshareError(null), 3000);
    } finally {
      setResharingId(null);
    }
  }, [documentId, permissions]);

  const fmtDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  };

  if (loading) return (
    <div className="analytics-dashboard loading">
      <div className="loading-spinner"><div className="spinner"></div><p>Loading analytics...</p></div>
    </div>
  );

  if (error) return (
    <div className="analytics-dashboard error">
      <div className="error-message">
        <h3>Analytics Unavailable</h3><p>{error}</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>Retry</button>
      </div>
    </div>
  );

  if (!analyticsData) return (
    <div className="analytics-dashboard no-data">
      <div className="no-data-message">
        <h3>No Analytics Data</h3>
        <p>Analytics will appear once users start viewing this document.</p>
      </div>
    </div>
  );

  // Prepare chart data
  const pageViewData = analyticsData.most_viewed_pages
    .slice(0, 10)
    .map(p => ({ name: `Page ${p.page_number}`, views: p.view_count }));

  const timelineData = analyticsData.activity_timeline
    .slice(-14)
    .map(t => ({
      date: new Date(t.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      Views: t.view_count,
      Downloads: t.download_count,
      Prints: t.print_count,
    }));

  const geoData = Object.entries(analyticsData.geographic_distribution)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 8)
    .map(([name, value]) => ({ name, value }));

  const activityData = [
    { name: 'Views', value: analyticsData.total_views, color: '#2c5aa0' },
    { name: 'Downloads', value: analyticsData.total_downloads, color: '#10b981' },
    { name: 'Prints', value: analyticsData.total_prints, color: '#f59e0b' },
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'pages', label: 'Page Views', icon: '📄' },
    { id: 'timeline', label: 'Timeline', icon: '⏱️' },
    { id: 'geography', label: 'Geography', icon: '🌍' },
  ];

  return (
    <div className="analytics-dashboard">
      <div className="dashboard-header">
        <h2>📈 Document Analytics {isAnonymous && <span className="anonymous-badge">Anonymous</span>}</h2>
        <div className="quick-stats">
          {[
            { value: analyticsData.total_views, label: 'Total Views' },
            { value: analyticsData.unique_viewers, label: 'Unique Viewers' },
            { value: `${Math.round(analyticsData.completion_rate)}%`, label: 'Completion Rate' },
            { value: `${Math.round(analyticsData.avg_session_duration / 60000)}m`, label: 'Avg. Session' },
            { value: analyticsData.total_downloads, label: 'Downloads' },
            { value: analyticsData.total_prints, label: 'Prints' },
            { value: analyticsData.total_reshares, label: 'Re-Shares' },
          ].map(({ value, label }) => (
            <div className="stat-card" key={label}>
              <div className="stat-value">{value}</div>
              <div className="stat-label">{label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="tab-navigation">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="charts-grid">
            <div className="chart-card">
              <h3>Activity Breakdown</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={activityData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, value }) => `${name}: ${value}`}>
                    {activityData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3>Top Pages by Views</h3>
              {pageViewData.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={pageViewData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="views" fill="#2c5aa0" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="no-chart-data">No page view data yet.</p>}
            </div>

            <div className="chart-card full-width">
              <h3>Key Metrics</h3>
              <div className="metrics-grid">
                {[
                  { label: 'Avg. Pages per Session', value: analyticsData.avg_pages_per_session.toFixed(1) },
                  { label: 'Drop-off Pages', value: analyticsData.drop_off_pages.length > 0 ? analyticsData.drop_off_pages.join(', ') : 'None' },
                ].map(({ label, value }) => (
                  <div className="metric-item" key={label}>
                    <span className="metric-label">{label}</span>
                    <span className="metric-value">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-tab-content">
            <div className="chart-card full-width">
              <h3>Unique Viewers ({viewers.length})</h3>
              {viewers.length === 0 ? (
                <p className="no-chart-data">No viewer data yet.</p>
              ) : (
                <table className="viewers-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>User</th>
                      <th>Page Views</th>
                      <th>Avg Time / Page</th>
                      <th>Total Time</th>
                      <th>Last Viewed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewers.map(viewer => (
                      <React.Fragment key={viewer.user_id}>
                        <tr
                          className={`viewer-row ${expandedUser === viewer.user_id ? 'expanded' : ''}`}
                          onClick={() => toggleUser(viewer.user_id)}
                        >
                          <td className="expand-cell">{expandedUser === viewer.user_id ? '▾' : '▸'}</td>
                          <td>
                            <div className="viewer-identity">
                              <span className="viewer-name">{viewer.display_name || viewer.email}</span>
                              {viewer.display_name && <span className="viewer-email">{viewer.email}</span>}
                            </div>
                          </td>
                          <td>{viewer.total_page_views}</td>
                          <td>{fmtDuration(viewer.avg_time_per_page_ms)}</td>
                          <td>{fmtDuration(viewer.total_session_duration_ms)}</td>
                          <td>{new Date(viewer.last_viewed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                        </tr>
                        {expandedUser === viewer.user_id && (
                          <tr className="drilldown-row">
                            <td colSpan={6}>
                              <div className="drilldown-content">
                                <h4>Page-level breakdown</h4>
                                {viewer.pages_viewed.length === 0 ? (
                                  <p className="no-chart-data">No page data available.</p>
                                ) : (() => {
                                    const sortedPages = [...viewer.pages_viewed].sort((a, b) => a.page_number - b.page_number);
                                    // Show every Nth label so X-axis stays readable at any page count
                                    const tickInterval = Math.max(0, Math.ceil(sortedPages.length / 10) - 1);
                                    const chartHeight = sortedPages.length > 20 ? 220 : 180;
                                    return (
                                      <>
                                        <div className="drilldown-table-scroll">
                                          <table className="pages-drilldown-table">
                                            <thead>
                                              <tr>
                                                <th>Page</th>
                                                <th>Views</th>
                                                <th>Avg Time Spent</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {sortedPages.map(p => (
                                                <tr key={p.page_number}>
                                                  <td>Page {p.page_number}</td>
                                                  <td>{p.view_count}</td>
                                                  <td>{fmtDuration(p.avg_duration_ms)}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                        <div className="drilldown-chart">
                                          <ResponsiveContainer width="100%" height={chartHeight}>
                                            <BarChart
                                              data={sortedPages.map(p => ({
                                                name: `P${p.page_number}`,
                                                'Avg Time (s)': +(p.avg_duration_ms / 1000).toFixed(1),
                                                Views: p.view_count,
                                              }))}
                                              margin={{ top: 4, right: 16, left: 0, bottom: sortedPages.length > 20 ? 24 : 4 }}
                                            >
                                              <CartesianGrid strokeDasharray="3 3" />
                                              <XAxis
                                                dataKey="name"
                                                tick={{ fontSize: 11 }}
                                                interval={tickInterval}
                                                angle={sortedPages.length > 20 ? -45 : 0}
                                                textAnchor={sortedPages.length > 20 ? 'end' : 'middle'}
                                              />
                                              <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11 }} />
                                              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                                              <Tooltip />
                                              <Legend />
                                              <Bar yAxisId="left" dataKey="Views" fill="#6366f1" radius={[3, 3, 0, 0]} />
                                              <Bar yAxisId="right" dataKey="Avg Time (s)" fill="#10b981" radius={[3, 3, 0, 0]} />
                                            </BarChart>
                                          </ResponsiveContainer>
                                        </div>
                                      </>
                                    );
                                  })()}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="chart-card full-width pending-viewers-card">
              <h3>⏳ Invited but Not Yet Opened ({pendingViewers.length})</h3>
              {pendingViewers.length === 0 ? (
                <p className="no-chart-data">All invited users have opened the document.</p>
              ) : (
                <table className="viewers-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Invited On</th>
                      <th>Permissions</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingViewers.map(p => (
                      <tr key={p.viewer_id} className="pending-row">
                        <td><span className="viewer-name">{p.viewer_email ?? p.viewer_id}</span></td>
                        <td>{new Date(p.granted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                        <td>
                          <span className="perm-badge">View</span>
                          {p.can_download && <span className="perm-badge">Download</span>}
                          {p.can_print && <span className="perm-badge">Print</span>}
                        </td>
                        <td className="reshare-cell">
                          {reshareSuccess === p.viewer_id ? (
                            <span className="reshare-feedback reshare-ok">✓ Sent</span>
                          ) : reshareError === p.viewer_id ? (
                            <span className="reshare-feedback reshare-err">✗ Failed</span>
                          ) : (
                            <button
                              className="btn-reshare"
                              disabled={resharingId === p.viewer_id}
                              onClick={() => handleReshare(p)}
                            >
                              {resharingId === p.viewer_id ? 'Sending…' : '↩ Re-Share'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {activeTab === 'pages' && (
          <div className="chart-card">
            <h3>Page View Distribution</h3>
            {pageViewData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={pageViewData} margin={{ top: 10, right: 30, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="views" fill="#2c5aa0" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="no-chart-data">No page view data yet.</p>}
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="chart-card">
            <h3>Activity Over Time</h3>
            {timelineData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={timelineData} margin={{ top: 10, right: 30, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="Views" stroke="#2c5aa0" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Downloads" stroke="#10b981" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Prints" stroke="#f59e0b" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : <p className="no-chart-data">No timeline data yet.</p>}
          </div>
        )}

        {activeTab === 'geography' && (
          <div className="charts-grid">
            <div className="chart-card">
              <h3>Views by Location</h3>
              {geoData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={geoData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} label={({ name, percent }) => `${((percent ?? 0) * 100).toFixed(0)}%`}>
                      {geoData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="no-chart-data">No geographic data yet.</p>}
            </div>

            <div className="chart-card">
              <h3>Top Locations</h3>
              {geoData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={geoData} layout="vertical" margin={{ top: 5, right: 30, left: 160, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={160} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#4a90e2" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="no-chart-data">No geographic data yet.</p>}
            </div>

            {geoData.length > 0 && (
              <div className="chart-card full-width">
                <h3>All Locations</h3>
                <table className="viewers-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Location</th>
                      <th>Views</th>
                      <th>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(analyticsData.geographic_distribution)
                      .sort(([, a], [, b]) => b - a)
                      .map(([location, count], i) => {
                        const total = Object.values(analyticsData.geographic_distribution).reduce((s, v) => s + v, 0);
                        return (
                          <tr key={location}>
                            <td style={{ color: '#888', width: 32 }}>{i + 1}</td>
                            <td>{location}</td>
                            <td>{count}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ flex: 1, height: 6, background: '#e0e0e0', borderRadius: 3 }}>
                                  <div style={{ width: `${(count / total) * 100}%`, height: '100%', background: '#4a90e2', borderRadius: 3 }} />
                                </div>
                                <span style={{ fontSize: '0.8rem', color: '#666', minWidth: 36 }}>{((count / total) * 100).toFixed(1)}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
